import { Text, View } from 'react-native'
import { QuerySuspenseBoundary } from '@/src/components/ui/QuerySuspenseBoundary'
import { SectionErrorState } from '@/src/components/ui/SectionErrorState'
import { SessionSummary } from '@/src/dal/session-dal'
import {
  hasSessionAnalysisMetrics,
  noteRangeLabel,
} from '@/src/features/statistics/utils/session-analysis-metrics'
import {
  backendSessionQueryOptions,
  backendSessionsQueryOptions,
} from '@/src/queries/session-queries'
import { colors } from '@/src/theme'
import { body4, headline3 } from '@/src/theme/typography'
import { captureException } from '@/src/utils/sentry'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

const cardSurfaceStyle = {
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
} as const

function DashboardVoiceAssessmentSummarySkeleton() {
  return (
    <View className="mb-3.5 w-full">
      <View className="w-full flex-row gap-2">
        <View
          testID="dashboard-voice-assessment-summary-score-skeleton"
          className="min-h-[75px] min-w-0 flex-1 basis-0 rounded-lg border border-gray-50 bg-background-secondary px-[18px] py-3"
          style={cardSurfaceStyle}
        >
          <View
            className="rounded"
            style={{
              width: '72%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-1 rounded"
            style={{
              width: 36,
              height: 23,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-1 rounded"
            style={{
              width: '52%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            }}
          />
        </View>
        <View
          testID="dashboard-voice-assessment-summary-range-skeleton"
          className="min-h-[75px] min-w-0 flex-1 basis-0 rounded-lg border border-gray-50 bg-background-secondary px-[18px] py-3"
          style={cardSurfaceStyle}
        >
          <View
            className="rounded"
            style={{
              width: '82%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-1 rounded"
            style={{
              width: 72,
              height: 23,
              backgroundColor: colors.buttonSecondary,
            }}
          />
          <View
            className="mt-1 rounded"
            style={{
              width: '52%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            }}
          />
        </View>
      </View>
    </View>
  )
}

function ReadyDashboardVoiceAssessmentSummary() {
  const { data: sessions } = useSuspenseQuery(backendSessionsQueryOptions)

  const completedSessions = sessions.filter(
    (session) => session.status === 'Completed',
  )
  if (!completedSessions.length) {
    return null
  }
  return (
    <ReadyDashboardVoiceAssessmentSummaryDetail sessions={completedSessions} />
  )
}

function ReadyDashboardVoiceAssessmentSummaryDetail({
  sessions,
}: {
  sessions: SessionSummary[]
}) {
  const { t } = useTranslation(['statistics'])

  const [latest] = sessions.sort(
    (a, b) => b.created.getTime() - a.created.getTime(),
  )

  const { data: session } = useSuspenseQuery(
    backendSessionQueryOptions(latest.id),
  )
  const placeholder = t('statistics:voice_assessment_placeholder_dash')
  if (!hasSessionAnalysisMetrics(session)) {
    return null
  }

  const singingScore =
    session.score == null ? placeholder : String(session.score)
  const comfortableRange =
    noteRangeLabel(session.comfortableMin, session.comfortableMax) ??
    placeholder

  return (
    <View className="mb-3.5 w-full">
      <View className="w-full flex-row gap-2">
        <View
          className="min-h-[75px] min-w-0 flex-1 basis-0 rounded-lg border border-gray-50 bg-background-secondary px-[18px] py-3"
          style={cardSurfaceStyle}
        >
          <Text style={[body4, { color: colors.textBody }]}>
            {t('statistics:voice_assessment_singing_score_label')}
          </Text>
          <Text style={[headline3, { color: colors.textHeader }]}>
            {singingScore}
          </Text>
          <Text style={[body4, { color: colors.textBody }]}>
            {t('statistics:voice_assessment_statistics_current_label')}
          </Text>
        </View>
        <View
          className="min-h-[75px] min-w-0 flex-1 basis-0 rounded-lg border border-gray-50 bg-background-secondary px-[18px] py-3"
          style={cardSurfaceStyle}
        >
          <Text style={[body4, { color: colors.textBody }]}>
            {t('statistics:voice_assessment_comfortable_range_label')}
          </Text>
          <Text style={[headline3, { color: colors.textHeader }]}>
            {comfortableRange}
          </Text>
          <Text style={[body4, { color: colors.textBody }]}>
            {t('statistics:voice_assessment_statistics_current_label')}
          </Text>
        </View>
      </View>
    </View>
  )
}

export function DashboardVoiceAssessmentSummary() {
  const { t } = useTranslation(['statistics', 'common'])

  return (
    <QuerySuspenseBoundary
      loadingFallback={<DashboardVoiceAssessmentSummarySkeleton />}
      errorFallback={({ reset }) => (
        <SectionErrorState
          testID="dashboard-voice-assessment-summary-error"
          title={t('statistics:voice_assessment_error_title')}
          message={t('statistics:voice_assessment_error_message')}
          onRetry={reset}
        />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'DashboardVoiceAssessmentSummary' },
        })
      }}
    >
      <ReadyDashboardVoiceAssessmentSummary />
    </QuerySuspenseBoundary>
  )
}
